// ---------- dictionnaire français ----------
// Toutes les chaînes du JEU (pas de l'admin, qui reste en dur) en un seul objet plat, organisé
// par fichier d'origine. Les valeurs à variables utilisent des espaces réservés `{{nom}}` — voir
// `t()` dans ./index.ts — et portent la phrase ENTIÈRE plutôt que des fragments recollés au
// runtime : une autre langue doit pouvoir réordonner ou reponctuer sans toucher au code.
//
// Ce fichier ne couvre PAS shared/alphabets, shared/difficulty.ts, shared/ink.ts : ces
// registres sont aussi lus par l'admin (qui doit rester en français en dur pour toujours), donc
// leurs libellés restent où ils sont — seule leur LECTURE, côté jeu, passe par alphabetLabel()/
// difficultyLabel()/inkLabel() dans ./index.ts. C'est le seul endroit à leur donner une table de
// correspondance dédiée le jour d'une deuxième langue.
const fr = {
  // ---------- marque (index.html, p.html, splash.ts) ----------
  // Jamais traduite d'une langue à l'autre, mais une vraie clé quand même : un rebranding ne
  // coûterait qu'une ligne, pas un grep sur tout le dépôt.
  'brand.name': 'Codigrid',

  // ---------- index.html <head> ----------
  'app.title': 'Codigrid — grille de motifs',
  'app.description': 'Jeu de coloriage logique : une image devient une grille de symboles codés.',

  // ---------- commun (plusieurs dialogues) ----------
  'common.close': 'Fermer',
  'common.cancel': 'Annuler',
  'common.back': 'Retour',

  // ---------- home.ts / index.html #home ----------
  'home.filter.doing': 'En cours',
  'home.filter.done': 'Terminés',
  'home.tab.feed': 'Mon flux',
  'home.tab.daily': 'Quotidien',
  'home.tab.library': 'Bibliothèque',
  'home.tab.settings': 'Réglages',
  'home.tab.create': 'Créer',
  'home.tab.new': 'Nouveau',
  'home.category.celebrite': 'Célébrité',
  'home.category.personnes': 'Portraits',
  'home.category.monuments': 'Monuments',
  'home.category.scenes': 'Scènes de la vie',
  'home.category.contes': 'Contes et légendes',
  'home.category.saisonnier': 'Saisonnier',
  'home.category.paysage': 'Paysage',
  'home.category.animaux': 'Animaux',
  'home.category.vehicules': 'Véhicules',
  'home.category.sport': 'Sport',
  'home.category.art': 'Art',
  'home.category.divers': 'Divers',
  'home.category.other': 'Autres',
  'home.facet.category.title': 'Catégorie',
  'home.facet.category.all': 'Tous',
  'home.facet.difficulty.title': 'Difficulté',
  'home.facet.difficulty.all': 'Toutes',
  'home.facet.alphabet.title': 'Alphabet',
  'home.facet.alphabet.all': 'Tous',
  'home.card.tooltip': '{{w}} × {{h}} cases · {{diff}}',
  'home.card.new': 'Nouveau',
  // ---------- la durée annoncée sur une carte (home.ts, shared/effort.ts) ----------
  // « ≈ » et non « environ » dans la ligne de la carte : elle fait 140 px et la place est celle
  // que les dimensions occupaient. Le mot entier vit dans l'infobulle, qui a de la place et qui
  // seule distingue le total du restant — sur la carte, l'anneau de progression le dit déjà.
  'home.card.eta': '≈ {{d}}',
  'home.card.etaLong': 'environ {{d}} en tout',
  'home.card.leftLong': 'environ {{d}} restant',
  'time.unit.min': 'min',
  'time.unit.h': 'h',
  'time.min': '{{n}} min',
  'time.h': '{{n}} h',
  'time.hmin': '{{h}} h {{m}}',
  'time.hplus': '{{n}} h+',
  // Pluriel : « cases » ne varie pas en français. Une traduction anglaise devra donner à cette
  // clé (et à game.finishedSub plus bas) une variante dénombrable — pas de moteur de pluriel
  // ajouté ici tant que le besoin n'existe pas.
  'home.empty.noMatch': 'Aucun puzzle ne correspond à ces filtres pour l’instant.',
  'home.empty.resetFilters': 'Réinitialiser les filtres',
  'home.empty.libraryDone': 'Tu as touché à tous les puzzles disponibles. Reviens : il en arrive.',
  // La Bibliothèque vide alors qu'il reste des packs à débloquer : le message pousse vers eux
  // au lieu de dire d'attendre (cf. paint() dans home.ts).
  'home.empty.libraryDonePacks': 'Tu as commencé tous tes puzzles. De nouveaux packs t’attendent.',
  'home.empty.seePacks': 'Voir les packs',
  'home.empty.puzzleGone': 'Ce puzzle est indisponible.',
  'home.empty.retry': 'Réessayer',
  'home.empty.noneDoing': 'Aucun puzzle en cours. Prends-en un dans la bibliothèque.',
  'home.empty.openLibrary': 'Ouvrir la bibliothèque',
  'home.empty.noneDone': 'Rien de terminé pour l’instant. Ça viendra.',
  'home.empty.noPuzzles': "Aucun puzzle disponible pour l'instant.",
  'home.empty.serverError': 'Impossible de contacter le serveur.',
  'home.loading': 'Chargement…',
  // ---------- la carte d'avis du flux (home.ts::rateCard, règles dans review.ts) ----------
  // Trois textes qui alternent d'une session à l'autre (review.ts::rateCardVariant) : l'avis qui
  // guide les autres joueurs, l'avis qui prend dix secondes, l'avis qui fait vivre le jeu. Aucun
  // ne présuppose une bonne note ni ne promet rien en échange — les règles de Play.
  'home.rate.1.title': 'Tu connais bien Codigrid maintenant',
  'home.rate.1.body':
    'Ton avis sur le Play Store aide d’autres amateurs de coloriage à savoir si le jeu est fait pour eux.',
  'home.rate.1.cta': 'Partager mon avis',
  'home.rate.2.title': 'Codigrid te fait passer un bon moment ?',
  'home.rate.2.body':
    'Dis-le en 10 secondes sur le Play Store : pour un petit jeu, ça change vraiment tout.',
  'home.rate.2.cta': 'Noter Codigrid',
  'home.rate.3.title': 'Envie de nouveaux puzzles ?',
  'home.rate.3.body':
    'Chaque avis sur le Play Store compte : c’est ce qui me permet de continuer à en créer.',
  'home.rate.3.cta': 'Donner mon avis',
  'home.progression.open': 'Progression',
  'home.packsBanner.sub': '{{n}} packs à débloquer',
  // Le bandeau de la Bibliothèque : le nombre de packs (« sub » en est devenu le titre, cf.
  // paintBanner dans home.ts), le singulier à part faute de pluriel dans t(), et son bouton.
  'home.packsBanner.one': '1 pack à débloquer',
  'home.packsBanner.cta': 'Découvrir',

  // ---------- les créations : home.ts (carte, filtre, cartes) et creer.ts (l'outil) ----------
  // « ta photo », « toi » : le tutoiement de tout le jeu. La confidentialité se dit DEUX fois
  // (première et dernière étape) et en clair, parce que c'est la question que pose quelqu'un à qui
  // on demande une photo de son chat ou de ses enfants — avant même de la choisir.
  'home.create.title': 'Créer mon puzzle',
  'home.create.sub': 'À partir d’une de tes photos',
  'home.facet.mine': 'Mes créations',
  // Les feuilles gratuites, hors de tout pack : le titre de leur groupe dans la Bibliothèque,
  // et la ligne d'origine de leurs cartes dans Mon flux (cf. packLabel dans home.ts).
  'home.pack.base': 'Pour commencer',
  'home.card.mine': 'Ma création',
  'home.empty.noCreations': 'Tu n’as pas encore créé de puzzle. Tes créations apparaîtront ici.',
  'home.creation.delete': 'Supprimer cette création',
  'home.creation.deleteConfirm':
    'Supprimer « {{title}} » ? Le puzzle et ta progression dessus seront perdus.',
  'creer.step': '{{n}} / {{total}}',
  'creer.next': 'Continuer',
  'creer.busy': 'Préparation de la feuille…',
  'creer.photo.title': 'Ta photo',
  'creer.photo.camera': 'Prendre une photo',
  'creer.photo.gallery': 'Ma galerie',
  'creer.photo.tipsTitle': 'Les photos qui font les plus beaux puzzles',
  'creer.photo.tip1': 'Un sujet qui remplit le cadre',
  'creer.photo.tip1Sub': 'Un visage, un animal, une silhouette, vus de près.',
  'creer.photo.tip2': 'Du contraste',
  'creer.photo.tip2Sub': 'Des clairs et des sombres bien marqués : ce sont eux qui dessinent.',
  'creer.photo.tip3': 'Une photo nette, en pleine lumière',
  'creer.photo.tip3Sub': 'Lumière du jour, ni contre-jour ni flou.',
  'creer.photo.tip4': 'Pas de tout petits détails',
  'creer.photo.tip4Sub': 'Un texte, une foule, un paysage lointain se perdent dans les cases.',
  'creer.photo.cutTitle': 'Un portrait ? Retire le fond d’abord',
  'creer.photo.cutText':
    'Avec un outil comme {{link}} par exemple, puis choisis l’image dans Ma galerie : le fond devient du papier.',
  'creer.photo.private':
    '<b>Ta photo n’est jamais envoyée.</b> La grille est fabriquée sur ton téléphone, et le puzzle n’est visible que par toi.',
  'creer.photo.error': 'Impossible de lire cette image. Essaie avec une autre photo.',
  'creer.crop.title': 'Cadrer',
  'creer.crop.hint': 'Pince pour zoomer, glisse pour déplacer',
  'creer.crop.photo': 'Photo',
  'creer.crop.square': 'Carré',
  'creer.crop.portrait': 'Portrait',
  'creer.crop.landscape': 'Paysage',
  'creer.crop.rotate': 'Pivoter d’un quart de tour',
  'creer.adjust.title': 'Ta feuille',
  'creer.tool.tech': 'Technique',
  'creer.tool.detail': 'Détail',
  'creer.tool.light': 'Lumière',
  'creer.light.normal': 'Normal',
  'creer.light.clear': 'Clair',
  'creer.light.notch': 'Lumière {{n}} sur {{total}}',
  'creer.tool.contrast': 'Contraste',
  'creer.contrast.soft': 'Naturel',
  'creer.contrast.strong': 'Franc',
  'creer.contrast.notch': 'Contraste {{n}} sur {{total}}',
  'creer.zoomReset': 'Revoir l’aperçu entier',
  'creer.detail.cells': 'Nombre de cases',
  'creer.detail.readout': '{{w}} × {{h}} · {{n}} cases',
  'creer.detail.notch': '{{n}} cases de côté',
  'creer.detail.less': 'Vite peint',
  'creer.detail.more': 'Plein de détails',
  'creer.ready.title': 'Récapitulatif',
  'creer.ready.name': 'Son nom',
  'creer.ready.placeholder': 'Mon puzzle',
  'creer.ready.photo':
    '<b>Ta photo reste sur ton téléphone.</b> Elle n’est jamais envoyée, et le puzzle qu’on en tire non plus.',
  'creer.ready.private':
    '<b>Ce puzzle n’est qu’à toi, sur ce téléphone.</b> Il n’est gardé nulle part ailleurs : si tu désinstalles le jeu ou changes de téléphone, il ne te suit pas.',
  'creer.ready.go': 'Enregistrer le puzzle',
  'creer.ready.saving': 'Enregistrement…',
  'creer.ready.storeFailed':
    'Impossible d’enregistrer le puzzle sur ce téléphone. Libère un peu de place et réessaie.',
  'creer.ready.tooMany':
    'Tu as déjà {{n}} créations, le maximum. Supprimes-en une depuis « Mes créations » pour en faire une nouvelle.',
  'creer.ready.full': 'La création de puzzles est momentanément indisponible. Réessaie plus tard.',
  'creer.ready.freeLeftMany': 'Encore {{n}} créations offertes, sans pub.',
  'creer.ready.freeLeftOne': 'Encore 1 création offerte, sans pub.',
  'creer.ready.adNote':
    'Ta première création était offerte. Celle-ci s’enregistre après une courte pub : c’est ce qui fait vivre le jeu.',
  'creer.ready.goAd': 'Voir une pub et enregistrer',
  'creer.ready.adSkipped':
    'Pub interrompue : ta création n’est pas encore enregistrée. Réessaie quand tu veux.',
  'creer.ready.adUnavailable': 'Aucune pub disponible pour l’instant. Réessaie dans un moment.',

  // ---------- daily.ts / index.html #dailyPage (le puzzle du jour) ----------
  'daily.month.prev': 'Mois précédent',
  'daily.month.next': 'Mois suivant',
  'daily.state.todo': 'À peindre',
  'daily.state.doing': 'En cours, {{pct}} %',
  'daily.state.done': 'Terminé',
  // La date et l'état d'une case, pour un lecteur d'écran : le motif ENTIER et non deux
  // fragments recollés, pour qu'une langue puisse les réordonner (cf. l'en-tête de ce fichier).
  'daily.cell.aria': '{{date}} — {{state}}',
  'daily.empty.stock': 'Aucun puzzle aujourd’hui. Reviens demain.',
  'daily.empty.update': 'Ce puzzle demande une version plus récente de l’application.',
  // La vedette de l'écran : la feuille du jour. Elle porte l'ACTION, et le calendrier sous elle
  // ne porte plus que la mémoire du mois.
  'daily.today.kicker': 'La feuille du jour',
  'daily.today.unknown': 'Personne ne sait encore ce que c’est',
  'daily.today.paint': 'Peindre',
  'daily.today.resume': 'Reprendre',
  'daily.today.see': 'Revoir',

  // ---------- packs.ts / index.html #packs (packs de puzzles à débloquer) ----------
  'packs.title': 'Packs disponibles',
  // Ce que le lecteur d'écran annonce de la bannière tournante (aria-roledescription).
  'packs.carousel': 'carrousel',
  'packs.loading': 'Chargement…',
  'packs.empty': 'Aucun pack à débloquer pour l’instant. Reviens : il en arrive.',
  // La technique quand le pack en mélange plusieurs : « 4 techniques » plutôt que la liste, qui
  // ne tiendrait pas sur la ligne d'un éventail.
  'packs.puzzlesLabel.one': 'puzzle',
  'packs.puzzlesLabel.other': 'puzzles',
  // Le surtitre d'une card sans catégorie : « Sélection » et non un blanc, sinon le titre
  // remonte et la card d'à côté ne s'aligne plus.
  'packs.selection': 'Sélection',
  'packs.unlock': 'Débloquer',
  // Android : le bouton dit le GESTE, la ligne en dessous dit le prix. Cf. unlockLabel().
  'packs.watchAd': 'Regarder une pub',
  // ---------- les arguments d'un pack (src/packArgument.ts) ----------
  // Neuf phrases, traduites une fois : c'est tout le mécanisme. Trois d'entre elles — mono,
  // varie, couleur — ne sont jamais cochées mais déduites de la composition du pack.
  'packs.arg.debutant': 'Pour débuter',
  'packs.arg.portraits': 'Des visages',
  'packs.arg.patience': 'Pour prendre son temps',
  'packs.arg.nouveau': 'Tout nouveau',
  'packs.arg.costaud': 'Grandes grilles',
  'packs.arg.express': 'Séances courtes',
  'packs.arg.mono': 'Une seule technique',
  'packs.arg.varie': 'Techniques variées',
  'packs.arg.couleur': 'En couleurs',
  'packs.unlocking': 'Déblocage…',
  // Sous le bouton Débloquer : rassure qu'il n'y a pas d'argent en jeu — cf. src/packs.ts,
  // unlockNote(). '<b>' est du balisage volontaire, comme home.intro plus bas.
  // Le prix, sous le bouton. Court : c'est une précision, pas une phrase à lire.
  'packs.adNote': '<b>Gratuit</b> · environ 30 s',
  'packs.freeNote': '<b>Gratuit</b>, en un tap',
  // Trois issues distinctes pour un déblocage raté, là où une seule phrase les couvrait toutes
  // — cf. handleUnlock() : 'adUnavailable' quand aucune pub n'a pu être chargée, 'adSkipped'
  // quand le joueur l'a refermée avant la récompense, 'adFailed' quand c'est l'appel au serveur
  // qui a échoué APRÈS une pub pourtant vue jusqu'au bout.
  'packs.adLoading': 'Préparation de la pub…',
  'packs.adUnavailable': 'Aucune pub dispo — réessaie.',
  'packs.adSkipped': 'Pub non terminée.',
  'packs.adFailed': 'Échec — réessaie.',
  'packs.reveal.title.one': '{{n}} puzzle débloqué !',
  'packs.reveal.title.other': '{{n}} puzzles débloqués !',
  'packs.reveal.sub': '« {{title}} » a rejoint ta Bibliothèque',
  'packs.reveal.continue': 'Continuer',
  // La vitrine des packs (2026-10-23, src/packs.ts) : la bannière, les étagères, la fiche.
  'packs.feature.coeur': 'Coup de cœur',
  'packs.feature.saison': 'De saison',
  'packs.feature.nouveau': 'Nouveau',
  'packs.feature.evenement': 'Événement',
  'packs.feature.joueurs': 'Coup de cœur des joueurs',
  'packs.feature.populaire': 'Le plus débloqué',
  'packs.volume': '{{title}} n°{{n}}',
  'packs.intro': '{{n}} puzzles à découvrir',
  'packs.shelf.n': '{{n}} packs',
  'packs.unlocks': 'Débloqué par {{n}} joueurs',
  'packs.fact.time': 'de coloriage',
  'packs.fact.votes': '{{n}} notes',
  'packs.fact.unrated': 'pas encore noté',
  'packs.inside': 'Ce qu’il contient',
  'packs.inside.hint': 'Les sujets se découvrent en coloriant.',
  'packs.sheet.puzzle': 'Puzzle {{n}}',
  // Ruban de la card « Éclat » (pack mono-technique, cf. src/packs.ts::renderList()).
  // Noms génériques par famille visuelle (src/techniqueIcon.ts::techniqueFamilyLabel()) —
  // distincts des libellés précis d'alphabetLabel() : utilisés quand plusieurs alphabetId d'une
  // même famille sont regroupés sous une seule illustration (vignettes de la card « Vitrine »,
  // sous-titre de la card « Éclat » si le pack mélange plusieurs alphabetId de la même famille).
  'technique.family.motifs': 'Motifs',
  'technique.family.hachures': 'Hachures',
  'technique.family.ascii': 'ASCII',
  'technique.family.pointille': 'Pointillé',
  'technique.family.des': 'Dés',
  'technique.family.briques': 'Briques',
  'technique.family.peinture': 'Peinture',
  'technique.family.croix': 'Broderie',

  // ---------- progression.ts / index.html #progression (badges + Ensemble) ----------
  'progression.title': 'Progression',
  'progression.tier.bronze': 'Bronze',
  'progression.tier.argent': 'Argent',
  'progression.tier.or': 'Or',
  'progression.tier.platine': 'Platine',
  'progression.loading': 'Chargement…',
  'progression.needAccount': 'Termine un puzzle pour voir ce que peignent les autres joueurs.',
  'progression.loadFailed': 'Impossible de contacter le serveur.',
  // L’écran « Moi » et ses badges (src/profile.ts) — les règles, seuils compris, viennent du serveur.
  'progression.tab.me': 'Moi',
  // L’onglet « Ensemble » (src/together.ts) et la rareté des badges — ce qui a remplacé la ligue.
  'progression.tab.together': 'Ensemble',
  'me.rarity': '{{pct}} % des joueurs',
  'me.rarity.rare': 'Rare · {{pct}} % des joueurs',
  'me.rarity.locked': 'Déjà obtenu par {{pct}} % des joueurs',
  'together.eyebrow': 'Cette semaine · depuis lundi',
  'together.sheets.one': 'puzzle terminé',
  'together.sheets.other': 'puzzles terminés',
  'together.players.one': 'par {{n}} joueur',
  'together.players.other': 'par {{n}} joueurs',
  'together.stats': 'La semaine en chiffres',
  'together.trend.eyebrow': 'Tendance',
  'together.trend.up': 'par rapport à la semaine dernière',
  'together.trend.same': 'comme la semaine dernière',
  'together.trend.calm': 'un peu plus calme que la semaine dernière',
  'together.trend.ctx': '{{n}} puzzles, contre {{last}} à la même heure la semaine dernière.',
  'together.peak.eyebrow': 'Heure de pointe',
  'together.peak.what': 'l’heure où l’on colorie le plus',
  'together.crossed.eyebrow': 'Badges',
  'together.crossed.one': 'palier franchi depuis lundi',
  'together.crossed.other': 'paliers franchis depuis lundi',
  'together.joined.eyebrow': 'Bienvenue',
  'together.joined.one': 'nouveau joueur cette semaine',
  'together.joined.other': 'nouveaux joueurs cette semaine',
  'together.joined.ctx': 'La semaine dernière à la même heure : {{last}}.',
  'together.empty':
    'Personne n’a encore fini de puzzle cette semaine. Le premier sera peut-être le tien.',
  'together.mine.title': 'Ta part de la semaine.',
  'together.mine.zero': 'Pas encore de puzzle cette semaine. Rien ne presse.',
  'together.mine.one': '1 puzzle terminé sur {{total}}.',
  'together.mine.other': '{{n}} puzzles terminés sur {{total}}.',
  'together.daily.one': '{{n}} joueur a déjà peint le puzzle du jour.',
  'together.daily.other': '{{n}} joueurs ont déjà peint le puzzle du jour.',
  'together.daily.with': 'Avec toi, {{n}} joueurs ont peint le puzzle du jour.',
  'together.daily.go': 'Le peindre',
  'progression.tier.prisme': 'Prisme',
  'me.eyebrow': 'Ton pseudo',
  'me.edit': 'Changer de pseudo',
  'me.choose': 'Choisis ton pseudo',
  'me.since': 'Joue depuis le {{date}}',
  'me.days.one': '{{n}} jour',
  'me.days.other': '{{n}} jours',
  'me.numbers': 'Tes chiffres',
  'me.cells.eyebrow': 'Cases peintes',
  'me.cells.what': 'depuis ton arrivée',
  'me.fav.eyebrow': 'Ton style',
  'me.fav.cat': 'Catégorie préférée : {{cat}}',
  'me.biggest.eyebrow': 'Ton plus grand puzzle',
  'me.biggest.cells': '{{n}} cases peintes',
  'me.biggest.untitled': 'Sans titre',
  'me.activity.title': 'Tes 12 dernières semaines',
  'me.activity.more': 'plus',
  'me.activity.less': 'moins',
  'me.activity.today': 'aujourd’hui',
  'me.activity.ago': 'il y a 12 semaines',
  'me.activity.how':
    'Une case par jour : plus elle est foncée, plus tu as fini de puzzles ce jour-là.',
  'me.activity.one': '1 jour avec au moins un puzzle fini',
  'me.activity.other': '{{n}} jours avec au moins un puzzle fini',
  'me.fav.none': 'À découvrir',
  'me.fav.noneHint': 'Termine un puzzle pour le révéler',
  'me.activity.none':
    'Pas encore de puzzle fini : chaque puzzle terminé foncera la case de son jour.',
  'together.cells.eyebrow': 'Cases peintes',
  'together.cells.what': 'par tout le monde, depuis lundi',
  'together.best.eyebrow': 'Meilleure journée',
  'together.best.one': '{{n}} puzzle terminé',
  'together.best.other': '{{n}} puzzles terminés',
  'together.top.eyebrow': 'Le mieux noté de la semaine',
  'together.top.votes.one': '{{n}} note',
  'together.top.votes.other': '{{n}} notes',
  'together.top.done': 'Tu l’as déjà peint',
  'together.top.locked': 'Dans un pack à débloquer',
  'together.top.unlock': 'Débloquer le pack',
  'me.stat.finished': 'puzzles finis',
  'me.stat.daily': 'puzzles du jour',
  'me.stat.styles': 'styles joués',
  'me.tally': 'paliers débloqués',
  'me.section.milestones': 'Jalons',
  'me.section.families': 'Alphabets',
  'me.section.categories': 'Catégories',
  'me.empty': 'Termine un puzzle pour débloquer tes premiers badges.',
  'badge.library': 'Bibliothèque',
  'badge.daily': 'Puzzle du jour',
  'badge.share': 'Partage',
  'badge.family.dots': 'Pointillisme',
  'badge.family.shapes': 'Formes',
  'badge.family.hatching': 'Hachures',
  'badge.family.dice': 'Dés',
  'badge.family.ascii': 'ASCII',
  'badge.family.bricks': 'Briques',
  'badge.family.paint': 'Peinture',
  'badge.family.embroidery': 'Point de croix',
  'badge.goal.library':
    'Termine des puzzles de la bibliothèque. Les puzzles du jour ont leur propre badge.',
  'badge.goal.daily':
    'Termine des puzzles du jour. Pas besoin de jours d’affilée : un jour rattrapé depuis le calendrier compte aussi.',
  'badge.goal.share':
    'Partage tes dessins terminés. Chaque feuille ne compte qu’une fois, même partagée plusieurs fois.',
  'badge.goal.family': 'Termine des puzzles en {{alphabets}}.',
  'badge.goal.category': 'Termine des puzzles de la catégorie {{category}}.',
  'badge.unit.puzzles.one': '{{n}} puzzle',
  'badge.unit.puzzles.other': '{{n}} puzzles',
  'badge.unit.shares.one': '{{n}} feuille',
  'badge.unit.shares.other': '{{n}} feuilles',
  'badge.next.bronze': 'pour le Bronze',
  'badge.next.argent': 'pour l’Argent',
  'badge.next.or': 'pour l’Or',
  'badge.next.platine': 'pour le Platine',
  'badge.next.prisme': 'pour le Prisme',
  'badge.reached': 'Palier {{tier}} atteint',
  'badge.earned': 'Obtenu',
  'badge.locked': 'À débloquer',

  // ---------- celebrate.ts / la carte d’un palier gagné ----------
  'celebrate.eyebrow': 'Nouveau palier',
  'celebrate.next': 'Suivant',
  'celebrate.later': 'Plus tard',
  'celebrate.see': 'Voir mes badges',
  'celebrate.skipped.one': '{{tiers}} franchi au passage',
  'celebrate.skipped.other': '{{tiers}} franchis au passage',
  'celebrate.more.eyebrow': 'Et aussi',
  'celebrate.more.title.one': '{{n}} autre palier',
  'celebrate.more.title.other': '{{n}} autres paliers',
  'celebrate.more.line': 'Tu les retrouves tous dans tes badges.',
  'celebrate.welcome.eyebrow': 'Nouveau : les badges',
  'celebrate.welcome.title': 'Tes badges sont arrivés',
  'celebrate.welcome.count': 'paliers déjà gagnés',
  'celebrate.welcome.line': 'Ils comptent tout ce que tu as colorié jusqu’ici.',

  // ---------- prefs.ts / la page Réglages (index.html #prefsPage) ----------
  // Pas de clé de titre : la page n'en porte pas, l'onglet allumé le dit (cf. la note de
  // #prefsPage). 'prefs.title' vivait ici pour l'en-tête du dialogue, qui n'existe plus.
  // « Français »/« English » eux-mêmes ne sont PAS des clés : le nom d'une langue s'affiche
  // dans elle-même, jamais traduit — texte brut posé dans index.html (cf. src/i18n/index.ts).
  'prefs.lang.label': 'Langue',
  'prefs.lang.hint': "Change tous les textes de l'application",
  'prefs.skin.label': 'Thème',
  'prefs.skin.hint': "L'habillage de l'écran — la feuille, elle, ne change pas",
  'prefs.sound.label': 'Son',
  'prefs.sound.hint': 'Une note par case remplie',
  'prefs.haptics.label': 'Vibrations',
  'prefs.haptics.hint': 'Retour dans la main à chaque case',
  'prefs.motion.label': 'Animations',
  'prefs.motion.hint': 'Éclosion des cases, particules, secousses',
  'prefs.compass.label': 'Boussole',
  'prefs.compass.hint': 'Une flèche vers le symbole quand il n’est plus en vue',
  'prefs.push.label': 'Notifications',
  'prefs.push.hint': 'Nouveaux puzzles, rappels',
  'prefs.inks.label': 'Couleur des cases',
  'prefs.inks.hint': 'De quoi la feuille se remplit',
  'prefs.marks.label': 'Repère des cases',
  'prefs.marks.hint': 'Comment les cases restantes se signalent',
  'prefs.markHues.label': 'Couleur du repère',
  'prefs.markHues.hint': "Elle ne touche que le repère, pas l'encre",
  'prefs.username.label': 'Pseudo',
  'prefs.username.hint': 'Visible si un jour tu veux le montrer',
  'prefs.username.placeholder': 'Choisis un pseudo',
  'prefs.username.random': 'Me proposer un pseudo',
  'prefs.username.save': 'Valider',
  'prefs.username.saving': 'Enregistrement…',
  'prefs.username.taken': 'Ce pseudo est déjà pris.',
  'prefs.username.rejected': 'Ce pseudo ne peut pas être utilisé. Choisis-en un autre.',
  'prefs.username.failed': 'Échec — réessaie une fois connecté.',
  // ---------- le code de reprise (prefs.ts) ----------
  // « Retrouver » et non « sauvegarder » : la progression est DÉJÀ sauvegardée, elle l'est à chaque
  // case. Ce que ce code achète, c'est de la retrouver ailleurs — et c'est ce mot-là que cherche
  // quelqu'un qui vient de changer de téléphone.
  'prefs.recovery.label': 'Retrouver ma progression',
  'prefs.recovery.hint': 'Un code à garder, pour reprendre sur un autre téléphone',
  'prefs.recovery.mine':
    'Note ce code quelque part. Il ramène ta progression sur un autre appareil, ou après une réinstallation.',
  'prefs.recovery.loading': 'Un instant…',
  'prefs.recovery.offline': 'Code indisponible — réessaie une fois connecté.',
  'prefs.recovery.copy': 'Copier',
  'prefs.recovery.copied': 'Copié',
  'prefs.recovery.theirs':
    'Tu as déjà un code ? Tape-le ici : cet appareil reprendra cette progression, et ce que tu as peint ici est conservé.',
  // Sans compte de mots, et c'est délibéré : les codes posés avant le 2026-09-05 en font quatre,
  // les neufs en font cinq (backend/src/utils/recovery.ts), et les deux se retapent ici. Annoncer
  // un nombre ferait douter la moitié des joueurs de leur propre papier.
  'prefs.recovery.placeholder': 'les mots séparés par des tirets',
  'prefs.recovery.use': 'Restaurer',
  'prefs.recovery.restoring': 'Restauration…',
  'prefs.recovery.unknown': 'Ce code ne correspond à aucune progression.',
  'prefs.recovery.failed': 'Échec — réessaie une fois connecté.',
  'prefs.recovery.done': 'Progression retrouvée.',

  // ---------- maker.ts / le mot du créateur, en tête de Réglages ----------
  'maker.eyebrow': 'Le mot du créateur',
  'maker.title': 'Salut, moi c’est tidou44',
  'maker.who':
    'Développeur web indépendant, j’ai créé Codigrid seul, pour le plaisir de voir un dessin naître case par case.',
  'maker.ask':
    'La pub reste discrète, c’est un choix. Si le jeu te plaît, le partager ou le noter m’aide beaucoup : j’ai plein d’idées pour la suite.',
  'maker.askWeb':
    'La pub reste discrète, c’est un choix. Si le jeu te plaît, le partager m’aide beaucoup : j’ai plein d’idées pour la suite.',
  'maker.share': 'Partager le jeu',
  'maker.rate': 'Noter',
  'maker.contact': 'M’écrire',
  'maker.shareText':
    'Je te conseille Codigrid, un coloriage logique où la grille ne dit pas ce qu’elle cache. Disponible sur Android uniquement pour le moment.',
  'prefs.ads.label': 'Choix publicitaires',
  'prefs.ads.hint': 'Revenir sur ton consentement aux annonces',
  'prefs.privacy.label': 'Confidentialité',
  'prefs.privacy.hint': "Ce qui reste sur l'appareil, ce qui est envoyé",
  'prefs.wipe.label': 'Supprimer mes données',
  'prefs.wipe.hint': 'Le compte et toutes les sauvegardes, ici et sur le serveur',
  'prefs.wipe.confirm':
    'Supprimer le compte et toutes les sauvegardes ? Cette action est définitive.',
  'prefs.wipe.progress': 'Suppression…',
  'prefs.wipe.failed': 'Échec — rien n’a été supprimé. Réessaie une fois connecté.',

  // ---------- mark.ts (repère des cases, propre au jeu — pas dans shared/) ----------
  'mark.battant.label': 'Contour épais',
  'mark.battant.hint': 'Un bord épais, le dessin intact',
  'mark.hachures.label': 'Hachures',
  'mark.hachures.hint': 'Des rayures dans les cases',
  'mark.cases.label': 'Croix par case',
  'mark.cases.hint': 'Une croix au centre de chaque case',
  // ---------- skin.ts (le thème) ----------
  // Les trois noms sont des mots simples et non des descriptions : la pastille MONTRE le thème,
  // le mot ne fait que le nommer pour qu'on puisse en parler.
  'skin.clair': 'Clair',
  'skin.ciel': 'Ciel',
  'skin.soir': 'Soir',

  'mark.hue.citron': 'Citron',
  'mark.hue.ambre': 'Ambre',
  'mark.hue.rose': 'Rose',
  // Gabarits de collage title/aria-label pour les pastilles de repère (prefs.ts) — deux
  // ponctuations différentes aujourd'hui en dur dans le code, chacune devient sa propre clé.
  'mark.tooltip': '{{label}} — {{hint}}',
  'mark.ariaLabel': '{{label}}. {{hint}}',

  // ---------- coach.ts et gestureDialog.ts : ce que l'écran apprend au début ----------
  // Une phrase par leçon, jamais deux, et à l'impératif : elles nomment le geste et disent ce
  // qui va arriver, pas ce que le système fait. On désigne les cases par ce qu'on VOIT — leur
  // surbrillance — et non par le numéro du symbole : celui-ci change d'une feuille à l'autre, et
  // demander de lire un chiffre pour comprendre où appuyer, c'est une lecture de trop.
  'coach.boussole': 'Le symbole continue par là. Touche la flèche.',
  'coach.close': 'Masquer ce conseil',
  'ordre.later':
    'Les couleurs se posent <b>dans l’ordre</b> : des claires aux sombres, puis les blancs pour les reflets. Celle-ci viendra plus tard.',
  // Le dialogue de la première feuille (gestureDialog.ts). Même règle, un geste par phrase, le
  // verbe en gras : c'est lui que l'œil attrape entre les deux animations. « Glisse sans
  // lever le doigt » et jamais « maintiens » : un doigt qui reste immobile déplace la feuille.
  // Le renard du logo parle à la première ouverture (gestureDialog.ts) : il se présente, puis
  // dit le POURQUOI avant le comment — devant une grille de symboles, un nouveau venu peut
  // croire à un jeu de logique dont il ne comprend pas la règle (2026-10-30). Pas de nom
  // propre pour l'instant : « le renard de Codigrid » se change d'une ligne s'il en reçoit un.
  'coach.hello.title': 'Salut, bienvenue !',
  'coach.hello.body':
    'Moi, c’est le renard de Codigrid. Chaque grille cache un dessin : remplis ses cases, et il apparaît.',
  'coach.hello.next': 'Montre-moi',
  'coach.dlg.title': 'Deux façons de colorier',
  'coach.dlg.tap': '<b>Touche</b> une case en surbrillance : toute sa zone se remplit.',
  'coach.dlg.swipe': '<b>Glisse</b> sans lever le doigt : chaque zone traversée se remplit.',
  'coach.dlg.next': 'Suivant',
  // La troisième page du dialogue : se déplacer. « En partant d'une case qui n'est pas en
  // surbrillance » n'est pas une précaution de style : partir d'une case du symbole choisi
  // PEINT (c'est le balayage), partir d'ailleurs déplace — cf. strokeKind dans ui.ts.
  'coach.move.title': 'Pour te promener dans le dessin',
  'coach.move.drag':
    '<b>Fais glisser</b> la grille pour te déplacer, en partant d’une case qui n’est pas en surbrillance.',
  'coach.move.pinch': '<b>À deux doigts</b> : écarte-les pour zoomer, rapproche-les pour dézoomer.',
  'coach.dlg.go': 'C’est parti',
  // src/piece.ts : posée sur la feuille à la fin du recul de la première feuille. Le sujet n'est
  // pas nommé — la pièce d'initiation changera avec les saisons, la phrase doit lui survivre.
  // La fin de la pièce d'initiation (#pieceDone) : le renard félicite, montre la vitrine du jeu
  // et laisse choisir. Les noms d'onglet sont ceux de home.tab.* — un nouveau venu doit les
  // retrouver tels quels en bas de l'écran.
  'piece.done.title': 'Bravo !',
  'piece.done.body':
    'Ce n’était que son regard : le reste du dessin t’attend. Et ce n’est pas le seul.',
  'piece.done.packs': 'Des <b>centaines de dessins</b> à télécharger, rangés en packs',
  'piece.done.daily': 'Un <b>dessin du jour</b>, dans l’onglet Quotidien',
  'piece.done.create': 'Tes <b>propres puzzles</b>, à partir de tes photos',
  'piece.done.ask': 'On termine ce dessin ensemble, ou tu vas voir la bibliothèque ?',
  'piece.done.finish': 'Terminer ce dessin',
  'piece.done.library': 'Voir la bibliothèque',
  'intro.bigDone':
    'Ton premier dessin est fini ! Des centaines d’autres t’attendent dans la bibliothèque.',
  'piece.bravo': 'Bravo !',
  'piece.line': 'Ce n’était que son regard. Le reste t’attend.',
  'prefs.intro.label': 'Revoir l’initiation',
  'prefs.intro.hint': 'Le premier dessin, avec le renard',
  'prefs.coach.label': 'Revoir les gestes',
  'prefs.coach.hint': 'Colorier, zoomer, se déplacer, en animation',

  // ---------- update dialog (pwa.ts / index.html #update) ----------
  'update.title': 'Mise à jour disponible',
  'update.body':
    'Une nouvelle version de Codigrid est prête. Elle s’installe en rechargeant la page — ta progression est conservée.',
  'update.reload': 'Recharger',
  'update.reloading': 'Mise à jour…',

  // ---------- écran de jeu (ui.ts / index.html .app) ----------
  'game.close': 'Fermer la feuille',
  'game.finished': 'Terminé',
  'game.finishedSub': '{{todo}} cases · {{w}}×{{h}} · {{alphabet}}',
  // La ligne « Avec toi, … » de la carte de fin (ui.ts, showPeers).
  'game.peers.sheet': 'Avec toi, {{n}} joueurs ont terminé ce dessin.',
  'game.peers.daily': 'Avec toi, {{n}} joueurs ont peint le puzzle du jour.',
  'game.peers.first': 'Tu es parmi les premiers à terminer ce dessin !',
  'game.peers.none': 'Personne ne l’avait terminé avant toi !',
  'game.rate': 'Noter ce puzzle',
  // Les deux boutons portent maintenant une icône dans le balisage (cf. index.html) : la flèche
  // et le chevron qui ouvraient ces chaînes en faisaient double emploi.
  'game.replay': "Revoir l'animation",
  'game.share': 'Partager',
  'game.linkCopied': '✓ Lien copié',
  'shareSheet.image': 'Image',
  'shareSheet.imageSub': 'Le dessin terminé',
  'shareSheet.video': 'Vidéo',
  'shareSheet.videoSub': 'Le remplissage, en 10 secondes',
  'shareSheet.imageBusy': 'Préparation de l’image…',
  'shareSheet.videoBusy': 'On filme ton remplissage…',
  'shareSheet.imageReady': 'Ton image est prête !',
  'shareSheet.videoReady': 'Ta vidéo est prête !',
  'shareSheet.imageSend': 'Envoyer l’image',
  'shareSheet.videoSend': 'Envoyer la vidéo',
  'shareSheet.imageSaved': '✓ Image enregistrée',
  'shareSheet.videoSaved': '✓ Vidéo enregistrée',
  'shareSheet.other': 'Choisir autre chose',
  'shareSheet.failed': 'Impossible de préparer le fichier. Réessaie dans un instant.',
  'shareSheet.msg.mineVideo':
    'Ma photo devient un dessin, case après case. Fait sur Codigrid, gratuit sur Android :\n{{url}}',
  'shareSheet.msg.mineImage':
    'Ma photo transformée en coloriage sur Codigrid. Gratuit sur Android :\n{{url}}',
  'shareSheet.msg.video':
    'Regarde ce dessin apparaître case après case. Codigrid, gratuit sur Android :\n{{url}}',
  'shareSheet.msg.image': 'Colorié case par case sur Codigrid. Gratuit sur Android :\n{{url}}',
  // L'accroche est celle de la fiche du Store, mot pour mot (cf. ui.ts) — ne pas la reformuler
  // indépendamment ici.
  'game.shareText':
    "J'ai terminé une feuille sur Codigrid — un coloriage logique où la grille ne dit pas ce qu'elle cache.",

  // ---------- splash.ts ----------
  'splash.line1': 'Une case à la fois,',
  'splash.line2': "l'image se révèle",

  // ---------- report.ts (le plantage au démarrage) ----------
  // Le seul texte que le joueur voit quand le jeu n'a pas démarré — donc quand rien ne garantit
  // que le reste de l'interface existe. Court, sans reproche, et suivi du bouton
  // `home.empty.retry`, qui recharge : c'est tout ce qu'on peut lui offrir à ce moment-là.
  'crash.boot': 'Le jeu n’a pas réussi à démarrer.',

  // ---------- share.ts (texte injecté dans p.html au chargement) ----------
  'share.title': '« {{title}} », terminé',
  'share.meta': '{{w}} × {{h}} · {{alphabet}}',
  'share.docTitle': '{{title}} — Codigrid',

  // ---------- p.html (le squelette statique de la page de partage) ----------
  // La <meta name="description"> de p.html. Doublon assumé du texte écrit en dur dans le
  // fichier : celui-là est ce que lisent les robots (ils n'exécutent pas applyStaticI18n), et
  // celle-ci est ce que lit un navigateur une fois la page vivante.
  'share.page.metaDescription':
    'Codigrid, un coloriage logique : une grille de symboles codés, et le dessin qui apparaît sous les doigts.',
  'share.page.docTitleDefault': 'Une feuille terminée — Codigrid',
  'share.page.title': 'Une feuille terminée',
  // Contient du balisage (<em>) au milieu de la phrase, pas en queue : impossible à découper
  // proprement en deux clés sans casser l'ordre des mots dans une autre langue. Seule valeur du
  // dictionnaire à être posée via data-i18n-html plutôt que data-i18n.
  'share.page.pitch':
    "Codigrid est un coloriage logique. Chaque case porte un symbole codé, on les remplit un symbole à la fois, et le dessin apparaît sous la main. <em>La grille ne dit pas ce qu'elle cache.</em>",
  'share.page.play': 'Jouer maintenant',
  // Le texte QUI EST DANS le badge, pas une description de l'image : Google le traduit lui-même
  // dans les cinq fichiers de public/badge/, et `alt` doit dire ce qu'un voyant lit. Recopié
  // au mot près depuis la page de ressources de marque — ces libellés ne se retraduisent pas.
  'share.page.store': 'Disponible sur Google Play',
  'share.page.fine': 'Gratuit, sans compte. Une nouvelle feuille chaque jour.',
  'share.page.fail': "Ce dessin n'est plus disponible.",
  'share.page.again': '↻ Revoir le remplissage',
} as const;

export default fr;
export type I18nKey = keyof typeof fr;
